import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { readCookie, setCookie } from "@/lib/cookie";
import { useAuth } from "@/hooks/useAuth";
import extractedEn from "./messages/extracted-en.json";
import extractedZhCN from "./messages/extracted-zh-CN.json";

// Lightweight i18n. Two locales (en, zh-CN). Translation goes through the
// SWC extractor pipeline: every user-facing `te("English literal")` call
// gets hashed into `extracted-en.json` and auto-translated into
// `extracted-zh-CN.json` by `bin/lib/translate-watch.ts`.
//
// Locale resolution on first paint: cookie → localStorage → signed-in
// user.locale → navigator language → "en". setLocale() is the only
// supported mutation path; it updates context state, html.lang,
// localStorage, the cookie, and (when signed in) patchUser so the
// server-side session catches up.

export type Locale = "en" | "zh-CN";
export const LOCALES: readonly Locale[] = ["en", "zh-CN"];
export const DEFAULT_LOCALE: Locale = "en";
const LOCALE_STORAGE_KEY = "locale";
const LOCALE_COOKIE_KEY = "locale";
const LOCALE_CHANNEL_KEY = "abg:locale";

interface I18nContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function pickBrowserLocale(): Locale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  const tag = navigator.language || navigator.languages?.[0] || "";
  if (tag.toLowerCase().startsWith("zh")) return "zh-CN";
  return "en";
}

function normalizeLocale(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  if (lower === "zh" || lower.startsWith("zh-") || lower === "zh-cn") {
    return "zh-CN";
  }
  if (lower.startsWith("en")) return "en";
  return null;
}

function readPersistedLocale(): Locale | null {
  if (typeof document === "undefined") return null;
  const fromCookie = normalizeLocale(readCookie(LOCALE_COOKIE_KEY));
  if (fromCookie) return fromCookie;
  try {
    const fromStorage = normalizeLocale(
      localStorage.getItem(LOCALE_STORAGE_KEY),
    );
    if (fromStorage) return fromStorage;
  } catch {
    // localStorage unavailable (Safari private mode, sandboxed iframe)
  }
  return null;
}

function substitute(
  template: string,
  vars?: Record<string, string | number>,
): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const v = vars[name];
    return v == null ? `{${name}}` : String(v);
  });
}

export function I18nProvider({
  children,
  localeOverride,
}: {
  children: ReactNode;
  localeOverride?: Locale;
}) {
  const { user, patchUser } = useAuth();

  const [locale, setLocaleState] = useState<Locale>(() => {
    return (
      readPersistedLocale() ??
      normalizeLocale(user?.locale) ??
      pickBrowserLocale()
    );
  });

  // Reconcile when the auth session resolves with a different locale than
  // we initialized from local storage. Server-stored preference wins on
  // first sight; subsequent toggles drive both. Synchronous setState in
  // this effect is intentional — we're acting on a single authoritative
  // upstream signal, not a continuous synchronization loop.
  useEffect(() => {
    const fromUser = normalizeLocale(user?.locale);
    if (fromUser && fromUser !== locale) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocaleState(fromUser);
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, fromUser);
      } catch {
        // localStorage unavailable (Safari private mode, sandboxed iframe)
      }
      setCookie(LOCALE_COOKIE_KEY, fromUser);
    }
    // We deliberately do NOT depend on `locale` here — that would loop the
    // toggle path. Only react to the user's authoritative server value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.locale]);

  const effectiveLocale = localeOverride ?? locale;

  // Mirror locale onto <html lang> so CSS/SR/lang-aware features see it.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = effectiveLocale;
    }
  }, [effectiveLocale]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const applyExternalLocale = (value: string | null | undefined) => {
      const next = normalizeLocale(value);
      if (!next) return;
      setLocaleState((current) => (current === next ? current : next));
      setCookie(LOCALE_COOKIE_KEY, next);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === LOCALE_STORAGE_KEY) {
        applyExternalLocale(event.newValue);
      }
    };

    let channel: BroadcastChannel | null = null;
    if ("BroadcastChannel" in window) {
      channel = new BroadcastChannel(LOCALE_CHANNEL_KEY);
      channel.onmessage = (event) => {
        applyExternalLocale(event.data);
      };
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      channel?.close();
    };
  }, []);

  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next);
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, next);
      } catch {
        // localStorage unavailable (Safari private mode, sandboxed iframe)
      }
      setCookie(LOCALE_COOKIE_KEY, next);
      if (typeof window !== "undefined" && "BroadcastChannel" in window) {
        const channel = new BroadcastChannel(LOCALE_CHANNEL_KEY);
        channel.postMessage(next);
        channel.close();
      }
      if (user && user.locale !== next) {
        patchUser({ locale: next });
      }
    },
    [user, patchUser],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale: effectiveLocale, setLocale }),
    [effectiveLocale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useLocale(): Locale {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useLocale must be used inside <I18nProvider>");
  }
  return ctx.locale;
}

export function useSetLocale(): (next: Locale) => void {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useSetLocale must be used inside <I18nProvider>");
  }
  return ctx.setLocale;
}

// Hash-keyed dictionaries cp'd verbatim from the legacy Next.js app's
// extracted-messages/. extractedEn maps `hash → english`; extractedZhCN
// maps `hash → chinese`. The hashes are produced by next-intl's SWC
// extractor plugin (see bin/lib/translate-watch.ts which runs that same
// plugin on our source tree to keep them current).
//
// Runtime lookup: te("English literal") → reverse map (built once at
// module load) → hash → extractedZhCN[hash] → translation. English
// locale is the source of truth and passes through verbatim.
const EXTRACTED_EN_BY_HASH = extractedEn as Record<string, string>;
const EXTRACTED_ZH_BY_HASH = extractedZhCN as Record<string, string>;

const HASH_BY_ENGLISH: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const [hash, english] of Object.entries(EXTRACTED_EN_BY_HASH)) {
    if (typeof english === "string") m.set(english, hash);
  }
  return m;
})();

// Typographic-ellipsis normalization. A handful of legacy `te()` call sites
// use the U+2026 character ("…") while the extracted bundle was built from
// strings that used three ASCII dots ("..."). Direct lookup misses; a
// normalized retry catches it.
function swapEllipsis(s: string): string | null {
  if (s.includes("…")) return s.replace(/…/g, "...");
  if (s.includes("...")) return s.replace(/\.\.\./g, "…");
  return null;
}

function lookupExtracted(locale: Locale, literal: string): string | undefined {
  if (locale === "en") return literal;
  const direct = HASH_BY_ENGLISH.get(literal);
  if (direct) {
    const translated = EXTRACTED_ZH_BY_HASH[direct];
    if (translated) return translated;
  }
  const swapped = swapEllipsis(literal);
  if (swapped) {
    const sw = HASH_BY_ENGLISH.get(swapped);
    if (sw) {
      const translated = EXTRACTED_ZH_BY_HASH[sw];
      if (translated) return translated;
    }
  }
  return undefined;
}

// Rich-text element callbacks for `te.rich(...)`. Mirrors next-intl's
// `te.rich(literal, { tagName: chunks => <Component>{chunks}</Component> })`
// API verbatim so legacy call sites can be ported with no edits.
type RichElements = Record<string, (chunks: ReactNode) => ReactNode>;

// Parse a translated string with `<tag>content</tag>` markup into a React
// node array, calling `elements[tag](chunks)` for each match. Tags must be
// non-nested simple `<name>...</name>` pairs (the format the legacy
// next-intl bundle emits) — full XHTML parsing is out of scope.
function renderRich(template: string, elements: RichElements): ReactNode {
  const parts: ReactNode[] = [];
  const re = /<([a-zA-Z][a-zA-Z0-9]*)>([\s\S]*?)<\/\1>/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(template))) {
    if (match.index > cursor) {
      parts.push(template.slice(cursor, match.index));
    }
    const tag = match[1];
    const content = match[2];
    const render = elements[tag];
    parts.push(
      <Fragment key={key++}>
        {render ? render(content) : `<${tag}>${content}</${tag}>`}
      </Fragment>,
    );
    cursor = match.index + match[0].length;
  }
  if (cursor < template.length) parts.push(template.slice(cursor));
  return parts.length === 1 ? parts[0] : parts;
}

interface ExtractedTranslator {
  (literal: string, vars?: Record<string, string | number>): string;
  rich: (
    literal: string,
    elements: RichElements,
    vars?: Record<string, string | number>,
  ) => ReactNode;
}

export function useExtracted(): ExtractedTranslator {
  const locale = useLocale();
  return useMemo<ExtractedTranslator>(() => {
    const base: (
      literal: string,
      vars?: Record<string, string | number>,
    ) => string = (literal, vars) => {
      const translated = lookupExtracted(locale, literal) ?? literal;
      return substitute(translated, vars);
    };
    const rich: ExtractedTranslator["rich"] = (literal, elements, vars) => {
      const translated = lookupExtracted(locale, literal) ?? literal;
      return renderRich(substitute(translated, vars), elements);
    };
    return Object.assign(base, { rich });
  }, [locale]);
}
